import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from '../overlays/ModalShell';
import { 
  XMarkIcon, 
  CheckIcon, 
  CalendarIcon
} from '@heroicons/react/24/outline';
import DynamicAppointmentForm from './DynamicAppointmentForm';

// TypeScript interfaces
interface AppointmentFormData {
  patient_id: string;
  treatment_type_id: string;
  dentist_id: string;
  start_date: string;
  start_time: string;
  end_time: string;
  status: string;
  appointment_source: string;
  notes: string;
  mode: string;
}

interface NewAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (appointmentData: any) => Promise<void>;
  initialDate?: string;
  initialTime?: string;
  initialDuration?: number; // Duration in minutes
  preselectedPatient?: { id: string; firstName: string; lastName: string } | null;
}

const NewAppointmentModal: React.FC<NewAppointmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialDate,
  initialTime,
  initialDuration = 30, // Default 30 minutes
  preselectedPatient = null
}) => {
  const { t } = useTranslation(['appointments', 'common']);
  
  // Helper function to calculate end time based on start time and duration
  const calculateEndTime = (startTime: string, durationMinutes: number): string => {
    if (!startTime) return '';
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    
    // Add duration in minutes
    const endDate = new Date(startDate.getTime() + (durationMinutes * 60000));
    
    // Format as HH:MM
    return endDate.toTimeString().slice(0, 5);
  };

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | null, text: string }>({ type: null, text: '' });
  const [formData, setFormData] = useState<AppointmentFormData>({
    patient_id: '',
    treatment_type_id: '',
    dentist_id: '',
    start_date: initialDate || new Date().toISOString().split('T')[0],
    start_time: initialTime || '09:00',
    end_time: calculateEndTime(initialTime || '09:00', initialDuration),
    status: 'Scheduled',
    appointment_source: 'Online',
    notes: '',
    mode: 'individual'
  });

  // Reset form when modal opens or when initial values change
  useEffect(() => {
    if (isOpen) {
      const startTime = initialTime || '09:00';
      const endTime = calculateEndTime(startTime, initialDuration);
      
      setFormData(prev => ({
        ...prev,
        patient_id: preselectedPatient?.id || '',
        start_date: initialDate || new Date().toISOString().split('T')[0],
        start_time: startTime,
        end_time: endTime,
      }));
      setMessage({ type: null, text: '' });
    }
  }, [isOpen, initialDate, initialTime, initialDuration, preselectedPatient]);

  // Handle form data changes and automatically update end time when start time changes
  const handleFormDataChange = (newFormData: AppointmentFormData) => {
    // Check if start_time changed and update end_time accordingly
    if (newFormData.start_time !== formData.start_time) {
      const currentDuration = getCurrentDuration();
      const newEndTime = calculateEndTime(newFormData.start_time, currentDuration);
      
      setFormData({
        ...newFormData,
        end_time: newEndTime
      });
    } else {
      setFormData(newFormData);
    }
  };
  
  // Helper function to get current duration based on start and end times
  const getCurrentDuration = (): number => {
    if (!formData.start_time || !formData.end_time) return initialDuration;
    
    const [startHours, startMinutes] = formData.start_time.split(':').map(Number);
    const [endHours, endMinutes] = formData.end_time.split(':').map(Number);
    
    const startDate = new Date();
    startDate.setHours(startHours, startMinutes, 0, 0);
    
    const endDate = new Date();
    endDate.setHours(endHours, endMinutes, 0, 0);
    
    // Handle case where end time is next day (past midnight)
    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    
    const durationMs = endDate.getTime() - startDate.getTime();
    return Math.round(durationMs / (1000 * 60));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.patient_id || !formData.start_date || !formData.start_time) {
      setMessage({ type: 'error', text: t('modal.messages.pleaseFillinRequiredFields') });
      return;
    }

    setLoading(true);
    try {
      // Combine date and time
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}:00`);
      const endDateTime = formData.end_time 
        ? new Date(`${formData.start_date}T${formData.end_time}:00`)
        : null;

      const appointmentData = {
        patient_id: formData.patient_id,
        treatment_type_id: formData.treatment_type_id || null,
        dentist_id: formData.dentist_id || null,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime?.toISOString() || null,
        status: formData.status,
        appointment_source: formData.appointment_source,
        notes: formData.notes,
        mode: formData.mode
      };

      await onSave(appointmentData);
      
      // Show success message
      setMessage({ type: 'success', text: t('modal.appointmentCreatedSuccess') });
      
      // Auto-close after 2 seconds to show the success message
      setTimeout(() => {
        onClose();
      }, 2000);
      
      // Reset form
      setFormData({
        patient_id: '',
        treatment_type_id: '',
        dentist_id: '',
        start_date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '',
        status: 'Scheduled',
        appointment_source: 'Online',
        notes: '',
        mode: 'individual'
      });
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      
      // Show error message from backend or default message
      const errorMessage = error?.message || error?.toString() || t('modal.appointmentCreateError');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell isOpen={isOpen} onClose={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-3xl">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-card-foreground flex items-center">
              <CalendarIcon className="h-6 w-6 mr-2 text-primary" />
              {t('modal.addTitle')}
            </h2>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Success/Error Message */}
        {message.type && (
          <div className={`mx-6 mt-4 p-4 rounded-lg border ${
            message.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
              : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
          }`}>
            <div className="flex items-center">
              {message.type === 'success' ? (
                <CheckIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              ) : (
                <XMarkIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              )}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <DynamicAppointmentForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            initialDate={initialDate}
            initialTime={initialTime}
            preselectedPatient={preselectedPatient}
          />

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 mt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary"
            >
              {t('common:cancel')}
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="btn-primary"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                  {t('modal.messages.creating')}
                </div>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  <span>{t('modal.messages.createAppointment')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
};

export default NewAppointmentModal;
